import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { QcIncidentResolvePanel } from "@/components/qc/QcIncidentResolvePanel";
import { QcIncidentViewModal } from "@/components/qc/QcIncidentViewModal";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { CardTitleWithHelp } from "@/components/ui/title-help";
import {
  QC_INCIDENT_HUB_MODULE_KEYS,
  QC_INCIDENT_MODE_QUERY_PARAM,
  QC_INCIDENT_QUERY_PARAM,
  writeQcIncidentOpenMode,
} from "@/lib/qc/qcIncidentNav";
import { useQcIncidentModal } from "@/lib/qc/qcIncidentModalContext";
import { useHubModuleWriteAny } from "@/lib/useHubModuleWrite";
import {
  QC_INCIDENTS_QUERY_KEY,
  QC_ROOT_CAUSES_QUERY_KEY,
  fetchIncident,
  fetchRootCauses,
} from "@/pages/manufacturing/qcIncidentsApi";
import { QualityAnalysisTransferPanel } from "@/pages/quality/QualityAnalysisTransferPanel";

export function QcIncidentModalHost(): JSX.Element | null {
  const writesAllowed = useHubModuleWriteAny(QC_INCIDENT_HUB_MODULE_KEYS);
  const { modal, closeQcIncident, setQcIncidentMode } = useQcIncidentModal();
  const [searchParams, setSearchParams] = useSearchParams();

  const incidentQuery = useQuery({
    queryKey: [...QC_INCIDENTS_QUERY_KEY, "detail", modal?.incidentId],
    queryFn: () => fetchIncident(modal!.incidentId),
    enabled: modal != null,
  });

  const rootCausesQuery = useQuery({
    queryKey: QC_ROOT_CAUSES_QUERY_KEY,
    queryFn: fetchRootCauses,
    enabled: modal?.mode === "resolve",
  });

  const handleClose = (): void => {
    if (searchParams.has(QC_INCIDENT_QUERY_PARAM) || searchParams.has(QC_INCIDENT_MODE_QUERY_PARAM)) {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete(QC_INCIDENT_QUERY_PARAM);
          writeQcIncidentOpenMode(next, null);
          return next;
        },
        { replace: true },
      );
    }
    closeQcIncident();
  };

  const openNamedMode = (mode: "resolve" | "loan"): void => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        writeQcIncidentOpenMode(next, mode);
        return next;
      },
      { replace: true },
    );
    setQcIncidentMode(mode);
  };

  useEffect(() => {
    if (modal == null) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        handleClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  });

  if (modal == null) {
    return null;
  }

  const incident = incidentQuery.data;
  const loadError = incidentQuery.error instanceof Error ? incidentQuery.error.message : null;

  return (
    <div
      {...hubModalOverlayProps(false)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="qc-incident-modal-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          handleClose();
        }
      }}
    >
      <div className="w-full max-w-3xl py-6">
        {incidentQuery.isLoading ? (
          <p className="rounded-lg border border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-600">
            Loading QC incident #{modal.incidentId}…
          </p>
        ) : loadError ? (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-6 text-sm text-rose-800">
            Could not load QC incident #{modal.incidentId}: {loadError}
          </div>
        ) : incident == null ? (
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-600">
            QC incident #{modal.incidentId} was not found.
          </div>
        ) : modal.mode === "resolve" ? (
          <QcIncidentResolvePanel
            incident={incident}
            rootCauses={rootCausesQuery.data?.rows ?? []}
            writesAllowed={writesAllowed}
            onClose={handleClose}
            onResolved={() => {
              void incidentQuery.refetch();
            }}
          />
        ) : modal.mode === "loan" ? (
          <Card>
            <CardHeader>
              <CardTitleWithHelp hoverHint="Send this unit out for analysis. The loan is its own record, linked to this QC incident.">
                Analysis loan
              </CardTitleWithHelp>
            </CardHeader>
            <CardBody className="space-y-4">
              <QualityAnalysisTransferPanel
                embedded
                hideHeading
                parent={{ qualityIncidentId: incident.id }}
                defaultSerial={incident.deviceLabelSerial ?? incident.serial ?? ""}
              />
              <div className="flex justify-end">
                <Button type="button" variant="secondary" onClick={handleClose}>
                  Close
                </Button>
              </div>
            </CardBody>
          </Card>
        ) : (
          <QcIncidentViewModal
            incident={incident}
            writesAllowed={writesAllowed}
            onClose={handleClose}
            onResolve={
              writesAllowed && incident.status === "unresolved" ? () => openNamedMode("resolve") : undefined
            }
            onAnalysisLoan={() => openNamedMode("loan")}
          />
        )}
      </div>
    </div>
  );
}
